import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnInit, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { MapApi } from '../../../api/map-api';
import { MCMapConstructor } from '../../../classes/mcmap';
import { MCMapViewConstructor } from '../../../classes/mcMapView';
import { ESRI } from '../../../esri-loader-util';
import { BooleanConverter, InputConverter } from '../../../inputConverter';
import { AoiService } from '../../../services/aoi.service';
import { LayerService } from '../../../services/layer.service';
import { BaseComponent } from '../../base.component';
import { DefaultMapComponent } from '../../mapComponents/map/map.component';

const defaultLatitude = -77.19428924374662;
const defaultLongitude = 40.83467074278475;
const defaultZoom = 7;

let Map: MCMapConstructor;
let MapView: MCMapViewConstructor;
let CoordinateFormatter: __esri.coordinateFormatter;
let Point: __esri.PointConstructor;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-lat-long',
    templateUrl: './lat-long.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './lat-long.component.scss']
})
export class LatLongComponent extends BaseComponent implements OnInit {
    @Input()
    private _latitude: number;
    get latitude() {
        return this._latitude;
    }
    set latitude(val) {
        this._latitude = val;
        this.createPoint();
    }

    @Input()
    private _longitude: number; get longitude() {
        return this._longitude;
    }
    set longitude(val) {
        this._longitude = val;
        this.createPoint();
    }

    @Input() @InputConverter(BooleanConverter) showDmsOption: boolean = false;
    inputOption: 'DD' | 'DMS' = 'DD';

    @Output() readonly hasSelectionChange = new EventEmitter<boolean>();

    latitudeDMS: { degrees: number; minutes: number; seconds: number } = { degrees: null, minutes: null, seconds: null};
    longitudeDMS: { degrees: number; minutes: number; seconds: number } = { degrees: null, minutes: null, seconds: null };
    latDmsStr: string;
    longDmsStr: string;

    hideMap: boolean;
    toggleMapButton: string;

    @ViewChild('latLongPicker') private readonly mapAreaRef: ElementRef<HTMLDivElement>;
    map;
    mapId; ElementRef;
    mapView;
    point;
    pointGraphic: __esri.Graphic;
    center: number[] = [defaultLatitude, defaultLongitude];
    zoom: number = defaultZoom;
    defaultComponents: DefaultMapComponent[] = ['zoom'];

    graphicsLayer;

    constructor(
        _vcr: ViewContainerRef,
        private readonly cdr: ChangeDetectorRef,
        private readonly mapApi: MapApi,
        private readonly layerService: LayerService,
        private readonly aoiService: AoiService,
        private readonly zone: NgZone
    ) {
        super(_vcr, true);
    }

    async ngOnInit() {
        [Map, MapView, CoordinateFormatter, Point] = await Promise.all([ESRI.MCMap, ESRI.MCMapView, ESRI.CoordinateFormatter, ESRI.Point]);
        this.hideMap = false;
        this.toggleMapButton = 'Hide Map';
        await CoordinateFormatter.load();
    }

    @Input() public activate = () => {
        if (!this.map) { this.createMap(); }
        this.hasSelectionChange.emit(Number.isFinite(this.latitude) && Number.isFinite(this.longitude));
    }

    @Input()
    public reset = () => {
        this._latitude = null;
        this._longitude = null;
        if (this.mapView) {
            this.hasSelectionChange.emit(false);
            this.mapView.graphics.removeAll();
            this.mapView.center = [defaultLatitude, defaultLongitude];
            this.mapView.zoom = defaultZoom;
        }
    }

    async createMap() {
        this.map = Map({ basemap: 'streets' });

        await this.zone.runOutsideAngular(async () => {
            this.mapView = await MapView({
                center: this.center,
                container: this.mapAreaRef.nativeElement,
                map: this.map,
                ui: { components: this.defaultComponents },
                zoom: this.zoom
            }, this.mapId, this.mapApi, this.layerService);
        });

        this.mapView.on('click', async (evt) => {
            this.latitude = evt.mapPoint.latitude;
            this.longitude = evt.mapPoint.longitude;

            //convert DD to DMS and update latitudeDMS & longitudeDMS
            const point = new Point({latitude: this.latitude, longitude: this.longitude});
            const dmsStr = CoordinateFormatter.toLatitudeLongitude(point, 'dms', 10);
            if (dmsStr) {
                const dmsArray = dmsStr.split(' ');
                this.latitudeDMS = { degrees: parseFloat(dmsArray[0]), minutes: parseFloat(dmsArray[1]), seconds: parseFloat(dmsArray[2].substring(0, dmsArray[2].length - 1)) };
                this.longitudeDMS = { degrees: parseFloat(dmsArray[3]), minutes: parseFloat(dmsArray[4]), seconds: parseFloat(dmsArray[5].substring(0, dmsArray[5].length - 1)) };
            }
            this.cdr.detectChanges();
        });
    }

    toggleMap() {
        if (this.hideMap) {
            this.hideMap = false;
            this.toggleMapButton = 'Hide Map';
        } else {
            this.hideMap = true;
            this.toggleMapButton = 'Show Map';
        }
    }

    async createPoint() {

        if (!this.latitude || !this.longitude) {
            return;
        }

        this.point = {
            type: 'point',
            latitude: this.latitude,
            longitude: this.longitude
        };

        const Graphic = await ESRI.Graphic;
        this.pointGraphic = new Graphic({
            geometry: this.point,
            symbol: {
                type: 'simple-marker',
                style: 'circle',
                color: 'yellow',
                size: '16px'
            } as any
        });

        this.hasSelectionChange.emit(true);
        this.mapView.graphics.removeAll();
        this.mapView.graphics.add(this.pointGraphic);
        this.mapView.goTo(this.pointGraphic);
    }

    @Input()
    public addResultToMap = async () => {
        this.aoiService.setSearchCriterias([{
            type: 'latLong',
            latitude: this.latitude,
            longitude: this.longitude
        }]);
    }

    async updateLatitudeDMS() {
        if (this.latitudeDMS.degrees !== null && this.latitudeDMS.minutes !== null && this.latitudeDMS.seconds !== null) {
            //convert DMS to DD and assign to lat
            this.latDmsStr = `${this.latitudeDMS.degrees}*${this.latitudeDMS.minutes}'${this.latitudeDMS.seconds}"N`;
            if (this.latDmsStr && this.longDmsStr) {
                const point = CoordinateFormatter.fromLatitudeLongitude(`${this.latDmsStr}${this.longDmsStr}`);
                this.latitude = point.latitude;
                this.longitude = point.longitude;
            }
        }
        return;
    }
    async updateLongitudeDMS() {
        if (this.longitudeDMS.degrees !== null && this.longitudeDMS.minutes !== null && this.longitudeDMS.seconds !== null) {
            //convert DMS to DD and assign to long
            this.longDmsStr = `${this.longitudeDMS.degrees}*${this.longitudeDMS.minutes}'${this.longitudeDMS.seconds}"W`;
            if (this.latDmsStr && this.longDmsStr) {
                const point = CoordinateFormatter.fromLatitudeLongitude(`${this.latDmsStr}${this.longDmsStr}`);
                this.latitude = point.latitude;
                this.longitude = point.longitude;
            }
        }
        return;
    }

}
